/**
 * Post-upgrade security options (letter-first Phase 8).
 *
 * Offered AFTER the user has something worth protecting — never as a
 * signup gate. Three honest paths:
 *   - authenticator app (existing TOTP setup, reused)
 *   - text-message codes (SMS 2FA — inline two-step enrollment with
 *     TCPA consent copy at opt-in)
 *   - decline, behind a risk-acknowledgment checkbox
 *
 * Self-gating: arriving from a flow (post-upgrade) with a decision
 * already recorded forwards straight to `next`. Arriving from Settings
 * (from='settings') always shows, so choices stay changeable forever.
 */
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { securityService, SecurityPreference } from '../../services/securityService';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';
import { TAP } from '../../utils/pointerWords';

const logger = createLogger('SecurityOptions');

type OptionsRouteProp = RouteProp<
  { params: { from?: 'flow' | 'settings'; letterId?: string } | undefined },
  'params'
>;

type SmsStep = 'closed' | 'phone' | 'code';

export const SecurityOptionsScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<OptionsRouteProp>();
  const from = route.params?.from ?? 'flow';

  const [pref, setPref] = useState<SecurityPreference | null>(null);
  const [loading, setLoading] = useState(true);
  const [smsStep, setSmsStep] = useState<SmsStep>('closed');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [working, setWorking] = useState(false);
  const [riskAccepted, setRiskAccepted] = useState(false);

  const finish = () => {
    if (from === 'settings') {
      navigation.goBack();
      return;
    }
    // Post-upgrade flow ends on Levels ("There's more here when you want
    // it") — the natural close: see what's unlocked, upsell in view, and
    // its exit link leads to the Dashboard.
    navigation.navigate('LetterLevels');
  };

  useEffect(() => {
    const load = async () => {
      try {
        const preference = await securityService.getPreference();
        // Already decided + arriving from a flow → nothing to ask.
        if (from === 'flow' && preference.decision !== null) {
          finish();
          return;
        }
        setPref(preference);
      } catch (error) {
        logger.error('Failed to load preference:', error);
        // Never block the flow on this screen's availability.
        if (from === 'flow') {
          finish();
          return;
        }
        Alert.alert('Error', 'Could not load your security settings.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      setPref(await securityService.getPreference());
    } catch (error) {
      logger.debug('Preference refresh failed:', error);
    }
  };

  const handleTotp = async () => {
    // Record the choice, then reuse the existing TOTP setup screen.
    securityService
      .recordDecision(pref?.sms2faEnabled ? 'both' : 'totp')
      .catch((e) => logger.debug('Decision record failed:', e));
    navigation.navigate('Auth', { screen: 'TotpSetup' });
  };

  const handleSendCode = async () => {
    if (working) return;
    try {
      setWorking(true);
      const result = await securityService.startSmsEnrollment(phone);
      setSmsStep('code');
      Alert.alert('Code sent', `We texted a 6-digit code to ${result.phoneMasked}.`);
    } catch (error: any) {
      Alert.alert('Error', error?.message || 'Could not send the code.');
    } finally {
      setWorking(false);
    }
  };

  const handleConfirmCode = async () => {
    if (working) return;
    try {
      setWorking(true);
      await securityService.confirmSmsEnrollment(code.trim());
      await securityService.recordDecision(pref?.totpEnabled ? 'both' : 'sms');
      setSmsStep('closed');
      await refresh();
      Alert.alert(
        'Text codes on',
        'From now on, signing in on a new device asks for a code we text you.',
        [{ text: 'Done', onPress: finish }],
      );
    } catch (error: any) {
      Alert.alert('Error', error?.message || 'Invalid code.');
    } finally {
      setWorking(false);
    }
  };

  const handleDecline = async () => {
    if (!riskAccepted || working) return;
    try {
      setWorking(true);
      await securityService.recordDecision('declined');
      finish();
    } catch (error: any) {
      Alert.alert('Error', error?.message || 'Could not save your choice.');
    } finally {
      setWorking(false);
    }
  };

  const handleDisableSms = () => {
    Alert.alert(
      'Turn off text codes?',
      'Signing in will no longer ask for a texted code.',
      [
        { text: 'Keep on', style: 'cancel' },
        {
          text: 'Turn off',
          style: 'destructive',
          onPress: async () => {
            try {
              await securityService.disableSms2fa();
              await refresh();
            } catch (error: any) {
              Alert.alert('Error', error?.message || 'Could not turn off text codes.');
            }
          },
        },
      ],
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        {from === 'settings' && (
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Image
              source={require('../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
        )}
        <Text style={styles.headerTitle}>Protect your account</Text>
        {from === 'settings' && <View style={styles.headerSpacer} />}
      </View>

      <View style={styles.sheet}>
        {loading ? (
          <View style={styles.loadingWrap}>
            <ActivityIndicator color="#29BFC0" size="large" />
          </View>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            <Text style={styles.intro}>
              Your letters and your family's information now live here. A second
              step at sign-in keeps them safe even if your password leaks.
            </Text>

            <TouchableOpacity
              style={styles.optionCard}
              onPress={handleTotp}
              accessibilityRole="button"
              accessibilityLabel="Use an authenticator app"
            >
              <View style={styles.optionText}>
                <Text style={styles.optionTitle}>
                  Authenticator app{pref?.totpEnabled ? '  ✓ on' : ''}
                </Text>
                <Text style={styles.optionSub}>
                  Codes from an app like Google Authenticator. Works without
                  cell service.
                </Text>
              </View>
            </TouchableOpacity>

            <View style={styles.optionCard}>
              <TouchableOpacity
                onPress={() =>
                  pref?.sms2faEnabled
                    ? handleDisableSms()
                    : setSmsStep(smsStep === 'closed' ? 'phone' : 'closed')
                }
                accessibilityRole="button"
                accessibilityLabel="Use text-message codes"
              >
                <View style={styles.optionText}>
                  <Text style={styles.optionTitle}>
                    Text-message codes
                    {pref?.sms2faEnabled ? `  ✓ on (${pref.sms2faPhoneMasked})` : ''}
                  </Text>
                  <Text style={styles.optionSub}>
                    {pref?.sms2faEnabled
                      ? `${TAP} to turn off.`
                      : 'A 6-digit code texted to your phone at sign-in.'}
                  </Text>
                </View>
              </TouchableOpacity>

              {smsStep === 'phone' && !pref?.sms2faEnabled && (
                <View style={styles.smsForm}>
                  <TextInput
                    style={styles.input}
                    placeholder="(555) 123-4567"
                    placeholderTextColor="#A0AEC0"
                    keyboardType="phone-pad"
                    value={phone}
                    onChangeText={setPhone}
                    accessibilityLabel="Phone number for text codes"
                  />
                  <Text style={styles.consent}>
                    By continuing you agree to receive sign-in codes by text at
                    this number. Message frequency varies with your sign-ins.
                    Msg&data rates may apply. Reply STOP to opt out, HELP for
                    help.
                  </Text>
                  <TouchableOpacity
                    style={[styles.smallButton, (working || phone.replace(/\D/g, '').length < 10) && styles.disabled]}
                    onPress={handleSendCode}
                    disabled={working || phone.replace(/\D/g, '').length < 10}
                    accessibilityRole="button"
                    accessibilityLabel="Text me a code"
                  >
                    {working ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.smallButtonText}>Text me a code</Text>
                    )}
                  </TouchableOpacity>
                </View>
              )}

              {smsStep === 'code' && !pref?.sms2faEnabled && (
                <View style={styles.smsForm}>
                  <TextInput
                    style={styles.input}
                    placeholder="6-digit code"
                    placeholderTextColor="#A0AEC0"
                    keyboardType="number-pad"
                    maxLength={6}
                    value={code}
                    onChangeText={setCode}
                    accessibilityLabel="Verification code"
                  />
                  <TouchableOpacity
                    style={[styles.smallButton, (working || code.trim().length !== 6) && styles.disabled]}
                    onPress={handleConfirmCode}
                    disabled={working || code.trim().length !== 6}
                    accessibilityRole="button"
                    accessibilityLabel="Confirm code"
                  >
                    {working ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.smallButtonText}>Confirm</Text>
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={handleSendCode}
                    disabled={working}
                    accessibilityRole="button"
                    accessibilityLabel="Resend code"
                  >
                    <Text style={styles.resendText}>Resend code</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>

            <View style={styles.declineBlock}>
              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => setRiskAccepted(!riskAccepted)}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: riskAccepted }}
                accessibilityLabel="I understand the risk of using a password only"
              >
                <View style={[styles.checkbox, riskAccepted && styles.checkboxOn]}>
                  {riskAccepted && (
                    <Image
                      source={require('../../assets/icons/check.png')}
                      style={styles.checkboxIcon}
                    />
                  )}
                </View>
                <Text style={styles.checkboxText}>
                  I understand my account will be protected by my password only,
                  and anyone who learns it could reach what I've stored here.
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.declineButton, (!riskAccepted || working) && styles.disabled]}
                onPress={handleDecline}
                disabled={!riskAccepted || working}
                accessibilityRole="button"
                accessibilityLabel="Continue without a second step"
                accessibilityState={{ disabled: !riskAccepted }}
              >
                <Text style={styles.declineButtonText}>
                  Continue without a second step
                </Text>
              </TouchableOpacity>
            </View>

            <Text style={styles.footnote}>
              You can change any of this later in Settings.
            </Text>

            <View style={styles.bottomPadding} />
          </ScrollView>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    height: s(56),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#fff',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerSpacer: {
    width: s(44),
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
    paddingHorizontal: ms(16),
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  intro: {
    fontSize: ms(15, 0.3),
    lineHeight: ms(22, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
    marginBottom: ms(16),
  },
  optionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(10),
  },
  optionText: {
    flex: 1,
  },
  optionTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  optionSub: {
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#6B7280',
    marginTop: ms(4),
  },
  smsForm: {
    marginTop: ms(12),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(50),
    borderWidth: ms(1),
    borderColor: '#E5E9F0',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    fontSize: ms(16, 0.3),
    color: '#2C4364',
    marginBottom: ms(10),
  },
  consent: {
    fontSize: ms(11, 0.3),
    lineHeight: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(10),
  },
  smallButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(12),
    alignItems: 'center',
  },
  smallButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  resendText: {
    textAlign: 'center',
    fontSize: ms(13, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
    paddingVertical: ms(10),
  },
  declineBlock: {
    marginTop: ms(10),
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: ms(10),
    marginBottom: ms(12),
  },
  checkbox: {
    width: s(22),
    height: s(22),
    borderRadius: ms(6),
    borderWidth: ms(2),
    borderColor: '#D8DBDD',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: ms(2),
  },
  checkboxOn: {
    backgroundColor: '#29BFC0',
    borderColor: '#29BFC0',
  },
  checkboxIcon: {
    width: s(13),
    height: s(13),
    tintColor: '#FFFFFF',
  },
  checkboxText: {
    flex: 1,
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#6B7280',
  },
  declineButton: {
    alignItems: 'center',
    paddingVertical: ms(13),
    borderRadius: ms(25),
    borderWidth: ms(1.5),
    borderColor: '#D8DBDD',
    backgroundColor: '#FFFFFF',
  },
  declineButtonText: {
    color: '#6B7280',
    fontSize: ms(15, 0.3),
    fontWeight: '600',
  },
  footnote: {
    fontSize: ms(12, 0.3),
    color: '#A9A9A9',
    textAlign: 'center',
    marginTop: ms(14),
  },
  disabled: {
    opacity: 0.5,
  },
  bottomPadding: {
    height: s(40),
  },
});

export default SecurityOptionsScreen;
